import { useCallback, useEffect, useRef } from 'react'
import { fetchCruiseOffer, fetchCruiseOfferDetail } from 'actions/CruiseActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

interface UseCruiseOfferDetailArgs {
  offerId: string | undefined
  enabled?: boolean
}

function getDepartureCount(departures?: Record<string, Array<unknown>>): number {
  return Object.keys(departures ?? {})
    .reduce((count, date) => count + (departures?.[date]?.length ?? 0), 0)
}

export function needsFullCruiseOffer(offer: App.CruiseOffer | undefined): boolean {
  if (!offer) return false

  return getDepartureCount(offer.departuresSummary) > 0 && getDepartureCount(offer.departures) === 0
}

export default function useCruiseOfferDetail({
  offerId,
  enabled = true,
}: UseCruiseOfferDetailArgs) {
  const dispatch = useAppDispatch()
  const attemptedOfferIdRef = useRef<string | undefined>(undefined)

  const offer = useAppSelector((state) => offerId ? state.cruise.cruiseOffers[offerId] : undefined)
  const isLoading = useAppSelector((state) => offerId ? !!state.cruise.cruiseOffersLoading[offerId] : false)
  const error = useAppSelector((state) => offerId ? state.cruise.cruiseOffersErrors[offerId] : undefined)
  const shouldFetchFullOffer = needsFullCruiseOffer(offer)
  const hasAttemptedFullOfferFetch = shouldFetchFullOffer && attemptedOfferIdRef.current === offerId
  const usableOffer = shouldFetchFullOffer && !hasAttemptedFullOfferFetch ? undefined : offer

  const fetchOffer = useCallback(() => {
    if (!offerId || !enabled) return
    attemptedOfferIdRef.current = offerId
    if (shouldFetchFullOffer) {
      dispatch(fetchCruiseOfferDetail(offerId))
      return
    }
    dispatch(fetchCruiseOffer(offerId))
  }, [dispatch, enabled, offerId, shouldFetchFullOffer])

  useEffect(() => {
    if (!enabled || !offerId) {
      attemptedOfferIdRef.current = undefined
      return
    }

    if (
      (offer && !shouldFetchFullOffer) ||
      hasAttemptedFullOfferFetch ||
      isLoading ||
      (error && attemptedOfferIdRef.current === offerId)
    ) return
    fetchOffer()
  }, [enabled, error, fetchOffer, hasAttemptedFullOfferFetch, isLoading, offer, offerId, shouldFetchFullOffer])

  return {
    offer: usableOffer,
    // Treat the pre-dispatch first-load frame as loading so the drawer never flashes empty content.
    isLoading: enabled && !!offerId && !error && (isLoading || !usableOffer),
    isError: !!error,
    refetch: fetchOffer,
  }
}
